import React, { FC, useCallback } from "react";
import { useIntl } from "react-intl";
import { Stack, Text } from "@fluentui/react";
import { IDropDown, useTheme } from "cayo.ui";
import { IUIItem, IFormField } from "@cayo/shared/api/types/schema";
import { RowPadding, ScrollableColumn } from "@cayo/shared/components/CommonLayout";
import { externalSubmitFunc } from "@cayo/shared/components/Form/types";
import { FilterItem } from "@cayo/shared/components/GridContainer/types";
import { commonMessages } from "@cayo/shared/messages/common";
import useService from "@cayo/shared/services/hooks/useService";
import { objectUtils } from "@cayo/shared/utils/object";
import Form from "../Form";
import ObjectIcon from "../ObjectIcon";
import Panel from "../Panel/Panel";

type FilterPanelProps = {
  title: string;
  onClose: () => void;
  onSave: (items: FilterItem[]) => void;
  items: IUIItem[];
  object: any;
};

export const FilterPanel: FC<FilterPanelProps> = ({ title, onClose, onSave, items, object }) => {
  const { cayoTheme } = useTheme();
  const actionBuilder = useService("actionBuilder");
  const intl = useIntl();

  const onSubmit = useCallback<externalSubmitFunc>(
    (p) => {
      const result = Object.keys(p)
        .filter((k) => !!p[k])
        .map((k) => {
          const item = items.find((i) => i.name === k) as IFormField;
          const options = (item as IDropDown)?.options;

          let value = p[k];
          const selectedOptionIndex = options?.findIndex((o) => o.key === value);

          if (selectedOptionIndex === 0) {
            return null;
          }

          const selectedOption = options && options[selectedOptionIndex];
          if (selectedOption) {
            value = selectedOption.text;
          } else if (value === (item as IFormField).defaultValue) {
            return null;
          }

          return {
            name: k,
            value: p[k],
            // value.name for FilterByOU
            displayValue: objectUtils.isObject(value) ? value.name : value,
            label: item.label!,
          } as FilterItem;
        })
        .filter((i) => !!i) as FilterItem[];

      onSave(result);
      onClose();

      return Promise.resolve();
    },
    [items]
  );

  return (
    <Panel type={"smallFluid"} isBlocking={true} onClose={onClose} isOpen={true}>
      <ScrollableColumn>
        {/* header TODO: icon  */}
        <div>
          <RowPadding>
            <ObjectIcon iconName="Filter" />
            <Text block variant="large" styles={{ root: { marginLeft: 20, marginTop: 10 } }}>
              {title}
            </Text>
          </RowPadding>
        </div>
        <Stack
          horizontal={false}
          verticalFill={true}
          tokens={cayoTheme.metrics.stackTokens}
          styles={{
            root: {
              padding: "20px 24px 24px 24px",
              flexGrow: 1,
              position: "relative",
              overflowY: "auto",
              height: "100%",
              display: "flex",
              overflowX: "hidden",
            },
          }}
        >
          <Form
            footer={{
              okText: intl.formatMessage(commonMessages.apply),
              cancelText: intl.formatMessage(commonMessages.cancel),
            }}
            items={items}
            type="json-form"
            actionBuilder={actionBuilder}
            onSubmit={onSubmit}
            onClose={onClose}
            object={object}
            sendAllFields={true}
          />
        </Stack>
      </ScrollableColumn>
    </Panel>
  );
};
